import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskPredictionPanel = ({ predictions }) => {
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-error text-error-foreground',
      high: 'bg-warning text-warning-foreground',
      medium: 'bg-accent text-accent-foreground',
      low: 'bg-success text-success-foreground'
    };
    return colors?.[severity] || colors?.medium;
  };

  const getRecommendationIcon = (type) => {
    const icons = {
      immediate: 'AlertCircle',
      scheduled: 'Calendar',
      monitor: 'Eye',
      escalate: 'ArrowUpCircle'
    };
    return icons?.[type] || 'Info';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
      <div className="p-16 md:p-20 lg:p-24 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <Icon name="Brain" size={20} className="text-warning" />
            </div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground font-heading">
              AI Risk Predictions
            </h3>
          </div>
          <span className="px-12 py-6 bg-warning/10 text-warning rounded-md text-sm font-medium caption">
            {predictions?.length} Alerts
          </span>
        </div>
      </div>
      <div className="divide-y divide-border max-h-96 md:max-h-[500px] overflow-y-auto">
        {predictions?.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-24 text-center">
            <Icon name="ShieldCheck" size={48} className="text-success mb-12" />
            <p className="text-muted-foreground">No risk predictions at this time</p>
          </div>
        ) : (
          predictions?.map((prediction) => (
            <div key={prediction?.id} className="p-16 md:p-20">
              <div className="flex items-start gap-12 mb-12">
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                  ${getSeverityColor(prediction?.severity)}
                `}>
                  <Icon name="AlertTriangle" size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-12 mb-8">
                    <h4 className="text-sm md:text-base font-medium text-foreground">
                      {prediction?.title}
                    </h4>
                    <span className="text-xs text-muted-foreground caption whitespace-nowrap">
                      {prediction?.probability}% probability
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-12">
                    {prediction?.description}
                  </p>

                  <div className="space-y-8">
                    <div className="flex items-start gap-8">
                      <Icon name="Target" size={16} className="text-muted-foreground flex-shrink-0 mt-2" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground caption mb-4">Impact:</p>
                        <p className="text-sm text-foreground">{prediction?.impact}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-8">
                      <Icon name="Lightbulb" size={16} className="text-muted-foreground flex-shrink-0 mt-2" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground caption mb-4">Recommended Action:</p>
                        <p className="text-sm text-foreground">{prediction?.recommendation}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-8 mt-12 pt-12 border-t border-border">
                    <Button
                      variant="default"
                      size="sm"
                      iconName={getRecommendationIcon(prediction?.actionType)}
                      iconPosition="left"
                      iconSize={16}
                    >
                      {prediction?.actionLabel}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      iconPosition="left"
                      iconSize={16}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RiskPredictionPanel;